import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { MentorRequiredAlert } from './MentorRequiredAlert.jsx';
import { OutstandingDocsAlert } from './OutstandingDocsAlert.jsx';
import { ApplicationDetail } from './ApplicationDetail.jsx';

// ─── HiringApplicationsView — manager review of /apply submissions ─────────
// List on the left, detail on the right (mobile: stacked). The manager rubric
// (6 signals × strong/ok/weak) and the "comment + decision" capture lives
// in the detail panel. Multi-manager visibility — any manager+ reads all
// cafés (CJ confirmed in HANDOFF.md "Resolved by CJ" #2).
export function HiringApplicationsView({ currentUser, locations, initialAppId = null }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [apps, setApps] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  // Hiring v3 / Ticket C6 — list filters + search.
  const [cafeFilter, setCafeFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [hasFlagsFilter, setHasFlagsFilter] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [archivedView, setArchivedView] = useState(false); // CJ 2026-07-29
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMsg, setSavingMsg] = useState('');

  const [loadError, setLoadError] = useState('');
  async function reloadList() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (cafeFilter) params.set('cafe_id', cafeFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      if (roleFilter) params.set('role', roleFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (hasFlagsFilter) params.set('has_flags', '1');
      if (archivedView) params.set('archived', '1');
      if (searchQ.trim().length >= 2) params.set('q', searchQ.trim());
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await api('GET', `/api/admin/applications${qs}`, null, headers);
      setApps(Array.isArray(r?.applications) ? r.applications : []);
      setLoadError('');
    } catch (e) {
      setApps([]);
      setLoadError(e?.message || String(e) || 'Could not load applications');
    } finally {
      setLoading(false);
    }
  }
  // Reload on any filter change. We deliberately do NOT clear the selection here:
  // with the inline accordion, a selectedId that isn't in the current list simply
  // doesn't expand (no separate pane to go blank), and NOT clearing lets a
  // deep-link to an archived app survive loadDetail flipping on the archived view.
  useEffect(() => { reloadList(); }, [statusFilter, cafeFilter, sourceFilter, roleFilter, dateFrom, dateTo, hasFlagsFilter, archivedView]);
  // Debounce search so each keystroke doesn't refetch.
  useEffect(() => {
    const t = setTimeout(() => { reloadList(); }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);
  function clearFilters() {
    setStatusFilter(''); setCafeFilter(''); setSourceFilter(''); setRoleFilter('');
    setDateFrom(''); setDateTo(''); setHasFlagsFilter(false); setSearchQ('');
  }
  const activeFilterCount =
    (statusFilter ? 1 : 0) + (cafeFilter ? 1 : 0) + (sourceFilter ? 1 : 0)
    + (roleFilter ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0)
    + (hasFlagsFilter ? 1 : 0) + (searchQ.trim().length >= 2 ? 1 : 0);

  async function loadDetail(id) {
    setSelectedId(id);
    setDetail(null);
    try {
      const r = await api('GET', `/api/admin/applications/${id}`, null, headers);
      setDetail(r);
      // If this app is archived but we're on the active list (e.g. a deep-link
      // from an email to an auto-archived candidate), switch to the archived
      // view so its row is present to expand — otherwise the detail has no row
      // to render under and the pane looks blank.
      if (r?.application?.archived_at && !archivedView) setArchivedView(true);
    } catch (e) {
      setDetail({ error: e.message || 'Could not load' });
    }
  }

  // Manager-alert email deep-link: when this view mounts with an
  // initialAppId (set by App from /admin/applications/:id), auto-open
  // the detail panel. Runs only once — manual list selection takes over
  // after that.
  useEffect(() => {
    if (initialAppId && !selectedId) {
      loadDetail(initialAppId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAppId]);

  async function postReview(payload) {
    setSavingMsg('Saving…');
    try {
      await api('POST', `/api/admin/applications/${selectedId}/review`, payload, headers);
      setSavingMsg('Saved.');
      setTimeout(() => setSavingMsg(''), 1800);
      await loadDetail(selectedId);
      await reloadList();
    } catch (e) {
      setSavingMsg(`Failed: ${e.message || e}`);
    }
  }

  async function blockApplicant() {
    const reason = window.prompt('Why are you blocking this applicant? (visible only to other managers in audit log)');
    if (!reason) return;
    try {
      await api('POST', `/api/admin/applications/${selectedId}/block`, { reason }, headers);
      setSavingMsg('Blocked. Future submissions from this email/phone will be silently rejected.');
      await loadDetail(selectedId);
      await reloadList();
    } catch (e) {
      alert(`Block failed: ${e.message || e}`);
    }
  }

  async function hireApplicant(form) {
    try {
      const r = await api('POST', `/api/admin/applications/${selectedId}/hire`, form, headers);
      setSavingMsg(`Hired. PIN: ${r.pin}. ${r.welcome_queued ? 'Welcome email sent.' : 'No email sent.'}`);
      await loadDetail(selectedId);
      await reloadList();
      return r;
    } catch (e) {
      alert(`Hire failed: ${e.message || e}`);
      throw e;
    }
  }

  async function deleteApplication() {
    const a = detail?.application;
    const who = a ? `${a.first_name} ${a.last_name}` : 'this application';
    if (!window.confirm(`Permanently delete the application from ${who}? Review history is preserved for audit.`)) return;
    try {
      await api('DELETE', `/api/admin/applications/${selectedId}`, null, headers);
      setSelectedId(null);
      setDetail(null);
      await reloadList();
    } catch (e) {
      alert(`Delete failed: ${e.message || e}`);
    }
  }
  async function unhireApplicant() {
    const a = detail?.application;
    const who = a ? `${a.first_name} ${a.last_name}` : 'this candidate';
    if (!window.confirm(
      `Unhire ${who}? Application returns to "under review", the user account is permanently deleted, ` +
      `and the email "${a?.email || ''}" is freed so it can be reused. This is for testing the hire flow.`
    )) return;
    try {
      const r = await api('POST', `/api/admin/applications/${selectedId}/unhire`, {}, headers);
      setSavingMsg(r.freed_email ? `Unhired. Email "${r.freed_email}" is now free.` : 'Unhired.');
      setTimeout(() => setSavingMsg(''), 3000);
      await loadDetail(selectedId);
      await reloadList();
    } catch (e) {
      alert(`Unhire failed: ${e.message || e}`);
    }
  }

  // E4: manager fix-up for contact fields on a submitted application
  // (typo'd phone or email). Allowlist enforced server-side; just
  // surfaces a save here and refreshes detail on success.
  async function editContact(patch) {
    setSavingMsg('Saving…');
    try {
      await api('PUT', `/api/admin/applications/${selectedId}`, patch, headers);
      setSavingMsg('Saved.');
      setTimeout(() => setSavingMsg(''), 1800);
      await loadDetail(selectedId);
    } catch (e) {
      setSavingMsg(`Failed: ${e.message || e}`);
      throw e;
    }
  }

  // Hiring v3 / Ticket B4 — Non-exclusive claim. Last-write-wins; if another
  // manager already claims the app, this transfers (and logs a transfer
  // event in the activity bar so the previous claimer can see what
  // happened).
  async function claimApp() {
    const a = detail?.application;
    const prevName = a?.claimed_by_name;
    if (prevName && prevName !== currentUser?.name) {
      if (!window.confirm(`${prevName} is currently leading this one. Take over?`)) return;
    }
    try {
      await api('POST', `/api/admin/applications/${selectedId}/claim`, {}, headers);
      await loadDetail(selectedId);
      await reloadList();
    } catch (e) {
      alert(`Claim failed: ${e.message || e}`);
    }
  }
  async function unclaimApp() {
    try {
      await api('POST', `/api/admin/applications/${selectedId}/unclaim`, {}, headers);
      await loadDetail(selectedId);
      await reloadList();
    } catch (e) {
      alert(`Unclaim failed: ${e.message || e}`);
    }
  }

  // Claim-or-Pass (CJ 2026-07-29): pass is a per-manager signal, not a decline.
  async function passApp({ reason, note }) {
    try {
      await api('POST', `/api/admin/applications/${selectedId}/pass`, { reason, note }, headers);
      await loadDetail(selectedId);
    } catch (e) { alert(`Pass failed: ${e.message || e}`); }
  }
  async function unpassApp() {
    try {
      await api('POST', `/api/admin/applications/${selectedId}/unpass`, {}, headers);
      await loadDetail(selectedId);
    } catch (e) { alert(`Withdraw failed: ${e.message || e}`); }
  }

  // Archive / restore (CJ 2026-07-29).
  async function archiveApp() {
    try {
      await api('POST', `/api/admin/applications/${selectedId}/archive`, {}, headers);
      await loadDetail(selectedId); await reloadList();
    } catch (e) { alert(`Archive failed: ${e.message || e}`); }
  }
  async function unarchiveApp() {
    try {
      await api('POST', `/api/admin/applications/${selectedId}/unarchive`, {}, headers);
      await loadDetail(selectedId); await reloadList();
    } catch (e) { alert(`Restore failed: ${e.message || e}`); }
  }

  const STATUS_LABELS = {
    submitted: 'New', under_review: 'Reviewing', contacted: 'Contacted',
    interview_scheduled: 'Interview set', interviewed: 'Interviewed',
    hired: 'Hired', passed: 'Passed', withdrawn: 'Withdrawn',
  };

  // Hiring v3 / Ticket F3 — Mentor-required alert. Lazy-loads the list of
  // recent hires without a mentor; surfaces a banner above the list.
  const [mentorAlerts, setMentorAlerts] = useState([]);
  // Hiring v3 / Ticket G4 — outstanding-onboarding-documents alert.
  const [outstandingDocs, setOutstandingDocs] = useState([]);
  useEffect(() => {
    api('GET', '/api/admin/hires-needing-mentors', null, headers)
      .then(r => setMentorAlerts(Array.isArray(r?.hires) ? r.hires : []))
      .catch(() => setMentorAlerts([]));
    api('GET', '/api/admin/onboarding-documents-outstanding', null, headers)
      .then(r => setOutstandingDocs(Array.isArray(r?.outstanding) ? r.outstanding : []))
      .catch(() => setOutstandingDocs([]));
  }, []);

  // CJ 2026-05-21: Google Appointment Schedule URL lives on the Hiring tab
  // (not in the profile) so the manager can paste/update it without tab-
  // switching. Per-user — each manager has their own booking page.
  const [apptUrl, setApptUrl] = useState('');
  const [apptUrlEditing, setApptUrlEditing] = useState(false);
  const [apptUrlDraft, setApptUrlDraft] = useState('');
  const [apptUrlMsg, setApptUrlMsg] = useState('');
  useEffect(() => {
    api('GET', `/api/profile/${currentUser.id}`, null, headers)
      .then(r => setApptUrl((r?.profile?.google_appointment_url || '').trim()))
      .catch(() => {});
  }, [currentUser.id]);
  async function saveApptUrl() {
    const v = (apptUrlDraft || '').trim();
    setApptUrlMsg('Saving…');
    try {
      await api('PUT', `/api/profile/${currentUser.id}`,
        { google_appointment_url: v || null }, headers);
      setApptUrl(v);
      setApptUrlEditing(false);
      setApptUrlMsg('Saved.');
      setTimeout(() => setApptUrlMsg(''), 1500);
    } catch (e) { setApptUrlMsg(`Failed: ${e.message || e}`); }
  }

  return (
    <div className="space-y-4">
      {mentorAlerts.length > 0 && (
        <MentorRequiredAlert hires={mentorAlerts} headers={headers}
          onAssigned={() => {
            api('GET', '/api/admin/hires-needing-mentors', null, headers)
              .then(r => setMentorAlerts(Array.isArray(r?.hires) ? r.hires : []))
              .catch(() => {});
          }} />
      )}
      {outstandingDocs.length > 0 && <OutstandingDocsAlert docs={outstandingDocs} />}

      {/* Per-manager Google Appointment Schedule URL editor. Paste the
          public booking URL from Google Calendar → Appointment Schedule
          here; it auto-injects into invite-to-interview emails. CJ
          2026-05-21: moved from the profile to the hiring tab so
          managers don't have to switch tabs to set it up. */}
      <div className="card" style={{ padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#212121' }}>
              📅 Your interview booking link
            </div>
            <div style={{ fontSize: 11, color: '#757575', marginTop: 2 }}>
              Paste your Google Calendar → Appointment Schedule share URL. The
              "Invite to interview" email injects it so candidates pick a slot
              from your calendar (with Google Meet or in-person, your choice).
            </div>
          </div>
          {!apptUrlEditing && (
            <button onClick={() => { setApptUrlDraft(apptUrl); setApptUrlEditing(true); setApptUrlMsg(''); }}
              className="btn-secondary" style={{ fontSize: 12 }}>
              {apptUrl ? 'Edit' : 'Add'}
            </button>
          )}
        </div>
        {apptUrlEditing ? (
          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="url" value={apptUrlDraft}
              onChange={e => setApptUrlDraft(e.target.value.trim())}
              placeholder="https://calendar.app.google/…"
              autoFocus
              style={{ flex: '1 1 300px', minWidth: 0, border: '1px solid #d0d4d9',
                        borderRadius: 6, padding: '6px 10px', fontSize: 13, fontFamily: 'inherit' }} />
            <button onClick={saveApptUrl}
              style={{ padding: '6px 14px', background: 'var(--forest)', color: '#fff',
                        border: 0, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Save
            </button>
            <button onClick={() => { setApptUrlEditing(false); setApptUrlMsg(''); }}
              className="btn-secondary" style={{ fontSize: 12 }}>
              Cancel
            </button>
            {apptUrlMsg && <span style={{ fontSize: 11, color: apptUrlMsg.startsWith('Failed') ? '#b00020' : '#666' }}>{apptUrlMsg}</span>}
          </div>
        ) : (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            {apptUrl ? (
              <a href={apptUrl} target="_blank" rel="noopener noreferrer"
                style={{ color: '#1565c0', wordBreak: 'break-all' }}>
                {apptUrl}
              </a>
            ) : (
              <span style={{ color: '#888', fontStyle: 'italic' }}>
                Not set. Without this, the invite email asks candidates to reply with times.
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">Hiring</h1>
          <p className="text-sm text-gray-500">Applications submitted via the public /apply form.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="search" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            placeholder="Search name, email, phone…"
            style={{ border: '1px solid #d0d4d9', borderRadius: 8, padding: '6px 10px',
                      fontSize: 13, minWidth: 200, fontFamily: 'inherit' }} />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
            <option value="">All statuses</option>
            <option value="submitted">New</option>
            <option value="under_review">Reviewing</option>
            <option value="contacted">Contacted</option>
            <option value="phone_screen_set">Phone screen set</option>
            <option value="interview_scheduled">Interview set</option>
            <option value="interviewed">Interviewed</option>
            <option value="hold_for_future">Hold for future</option>
            <option value="hired">Hired</option>
            <option value="passed">Passed</option>
          </select>
          <button onClick={() => setFiltersOpen(o => !o)}
            style={{ border: '1px solid #d0d4d9', borderRadius: 8, padding: '6px 12px',
                      fontSize: 13, background: filtersOpen ? '#fef9ee' : '#fff',
                      cursor: 'pointer', fontWeight: 600, color: '#333' }}>
            Filters{activeFilterCount > 0 && ` · ${activeFilterCount}`}
          </button>
          {/* CJ 2026-07-29: toggle the archived view (stale apps auto-archived
              after 20 days with no manager response). */}
          <button onClick={() => setArchivedView(v => !v)}
            style={{ border: '1px solid #d0d4d9', borderRadius: 8, padding: '6px 12px',
                      fontSize: 13, background: archivedView ? '#eceff1' : '#fff',
                      cursor: 'pointer', fontWeight: 600, color: '#333' }}>
            {archivedView ? '← Active' : '🗄 Archived'}
          </button>
        </div>
      </div>

      {/* Hiring v3 / Ticket C6 — expanded filter panel. Tap "Filters" above
          to toggle. Status + search live in the toolbar; this panel adds
          café, source-of-hire, role interest, date range, and "has flags". */}
      {filtersOpen && (
        <div style={{ marginBottom: 12, padding: 12, background: '#fafafa',
                      border: '1px solid #e0e0e0', borderRadius: 8,
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Café
            <select value={cafeFilter} onChange={e => setCafeFilter(e.target.value)}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }}>
              <option value="">Any café</option>
              {(locations || []).map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Role interest
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }}>
              <option value="">Any role</option>
              <option value="Barista">Barista</option>
              <option value="Shift Lead">Shift Lead</option>
              <option value="Kitchen">Kitchen</option>
              <option value="Roaster">Roaster</option>
              <option value="Manager">Manager</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Source
            <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }}>
              <option value="">Any source</option>
              <option value="walked_by">Walked by</option>
              <option value="customer">Customer first</option>
              <option value="referral">Referral</option>
              <option value="instagram">Instagram</option>
              <option value="indeed">Indeed</option>
              <option value="google">Google</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            From
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }} />
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            To
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }} />
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'end', paddingBottom: 4 }}>
            <input type="checkbox" checked={hasFlagsFilter}
              onChange={e => setHasFlagsFilter(e.target.checked)} />
            Has flags only
          </label>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters}
              style={{ alignSelf: 'end', border: '1px solid #d0d4d9', borderRadius: 6,
                        padding: '6px 10px', fontSize: 12, background: '#fff', cursor: 'pointer' }}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* CJ 2026-07-29: single-column accordion — selecting an applicant
          expands their full application INLINE right under their name, instead
          of a separate detail panel that stacked below the whole list (which
          forced a scroll to the bottom, especially on mobile). */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="text-sm text-gray-500 p-4">Loading…</div> :
         loadError ? <div className="p-4"><div style={{ fontSize: 12, color: '#b00020', background: '#fef2f2', border: '1px solid #fecaca', padding: 8, borderRadius: 6 }}>Failed to load applications: {loadError}</div></div> :
         apps.length === 0 ? <div className="text-sm text-gray-500 p-4">No applications {statusFilter ? `with status "${STATUS_LABELS[statusFilter] || statusFilter}"` : 'yet'}.</div> :
         apps.map(a => {
          const isOpen = a.id === selectedId;
          return (
          <div key={a.id} style={{ borderTop: '1px solid #f0f0f0',
                        borderLeft: isOpen ? '3px solid var(--gold)' : '3px solid transparent',
                        background: isOpen ? '#fef9ee' : 'transparent' }}>
            {/* Clickable header row — toggles the inline detail. */}
            <div role="button" tabIndex={0}
              onClick={() => isOpen ? (setSelectedId(null), setDetail(null)) : loadDetail(a.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); isOpen ? (setSelectedId(null), setDetail(null)) : loadDetail(a.id); } }}
              style={{ padding: 12, cursor: 'pointer', width: '100%', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontWeight: 600, color: '#212121', flex: 1, minWidth: 0 }}>
                  <span style={{ color: '#9e9e9e', marginRight: 6, fontSize: 11 }}>{isOpen ? '▾' : '▸'}</span>
                  {a.first_name} {a.last_name}
                </div>
                <span style={{ fontSize: 11, color: '#9e9e9e', flexShrink: 0 }}>
                  {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2, paddingLeft: 17 }}>
                {a.cafe_flexible ? 'flexible — any café' : (a.cafe_name || '(unknown café)')}
              </div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4, paddingLeft: 17, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>{STATUS_LABELS[a.status] || a.status}</span>
                {a.review_count > 0 && <span>· {a.review_count} review{a.review_count === 1 ? '' : 's'}</span>}
                {Array.isArray(a.flags) && a.flags.length > 0 && (
                  <span title={a.flags.map(f => f.label).join('\n')}
                    style={{ background: '#fef9ee', color: '#8e6c33', border: '1px solid #f5e6c8',
                              padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                    🏷 {a.flags.length} flag{a.flags.length === 1 ? '' : 's'}
                  </span>
                )}
                {a.claimed_by_user_id && (
                  <span title={`${a.claimed_by_name || 'A manager'} is leading this — non-exclusive`}
                    style={{ background: '#fff8e6', color: '#7a5b1a', border: '1px solid #f0d9a3',
                              padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                    👤 {a.claimed_by_name ? a.claimed_by_name.split(' ')[0] : 'led'}
                  </span>
                )}
              </div>
            </div>
            {/* Inline expanded detail for the selected applicant. */}
            {isOpen && (
              <div style={{ padding: '4px 12px 14px', borderTop: '1px solid #f5e6c8' }}>
                {!detail ? (
                  <div className="text-sm text-gray-500 p-2">Loading…</div>
                ) : detail.error ? (
                  <div className="text-sm text-red-600 p-2">{detail.error}</div>
                ) : (
                  <ApplicationDetail detail={detail} onPostReview={postReview}
                    onBlock={blockApplicant} onDelete={deleteApplication}
                    onHire={hireApplicant} onClaim={claimApp} onUnclaim={unclaimApp}
                    onPass={passApp} onUnpass={unpassApp}
                    onArchive={archiveApp} onUnarchive={unarchiveApp}
                    onEditContact={editContact} onUnhire={unhireApplicant}
                    locations={locations}
                    currentUser={currentUser} savingMsg={savingMsg} />
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
